import React, { createContext, useContext } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

const StaggerContext = createContext(null);

/**
 * Stagger parent — passes per-child delay context down.
 *   <Stagger gap={70} initialDelay={0}>
 *     <StaggerItem>…</StaggerItem>
 *     <StaggerItem>…</StaggerItem>
 *   </Stagger>
 */
export function Stagger({
  gap = 70,
  initialDelay = 0,
  as: As = "div",
  once = true,
  amount = 0.15,
  className,
  children,
  ...rest
}) {
  const ref = React.useRef(null);
  const inView = useInView(ref, { once, amount });
  return (
    <StaggerContext.Provider value={{ gap, initialDelay, inView }}>
      <As ref={ref} className={className} {...rest}>
        {React.Children.map(children, (child, i) => {
          if (!React.isValidElement(child)) return child;
          return React.cloneElement(child, { __staggerIndex: i });
        })}
      </As>
    </StaggerContext.Provider>
  );
}

export function StaggerItem({
  as: As = "div",
  className,
  children,
  __staggerIndex = 0,
  ...rest
}) {
  const reduce = useReducedMotion();
  const ctx = useContext(StaggerContext);
  const MotionAs = motion[As] ?? motion.div;

  if (reduce || !ctx) {
    return (
      <As className={className} {...rest}>
        {children}
      </As>
    );
  }

  const delay = (ctx.initialDelay + __staggerIndex * ctx.gap) / 1000;

  return (
    <MotionAs
      initial={{ opacity: 0, y: 24 }}
      animate={ctx.inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1], delay }}
      className={className}
      {...rest}
    >
      {children}
    </MotionAs>
  );
}

export default Stagger;
